export const DUPLICATE_STATUS_OPTIONS = [
  { value: '', label: 'Operacionais' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'confirmed', label: 'Confirmados' },
  { value: 'merged', label: 'Mesclados' },
  { value: 'rejected', label: 'Rejeitados' },
];

export const DUPLICATE_MATCH_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'exact', label: 'Exato' },
  { value: 'probable', label: 'Provavel' },
  { value: 'ambiguous', label: 'Ambiguo' },
];

const field = (object, snakeKey, camelKey = snakeKey) =>
  object?.[snakeKey] ?? object?.[camelKey];

const normalizeObject = value =>
  value && typeof value === 'object' ? value : {};

const normalizeImovel = value => {
  const payload = normalizeObject(value);
  return {
    id: field(payload, 'id'),
    missing: Boolean(field(payload, 'missing')),
    titulo: field(payload, 'titulo') || 'Imovel sem titulo',
    endereco: field(payload, 'endereco') || '',
    cidade: field(payload, 'cidade') || '',
    bairro: field(payload, 'bairro') || '',
    tipo: field(payload, 'tipo') || '',
    finalidade: field(payload, 'finalidade') || '',
    status: field(payload, 'status') || '',
    listingStatus: field(payload, 'listing_status', 'listingStatus') || '',
    valorVenda: field(payload, 'valor_venda', 'valorVenda'),
    valorAluguel: field(payload, 'valor_aluguel', 'valorAluguel'),
    externalId: field(payload, 'external_id', 'externalId') || '',
    updatedAt: field(payload, 'updated_at', 'updatedAt') || '',
  };
};

export const normalizeMergeOperation = operation => {
  const payload = normalizeObject(operation);
  if (!payload.id) return null;

  return {
    id: payload.id,
    status: field(payload, 'status') || '',
    winnerImovelId: field(payload, 'winner_imovel_id', 'winnerImovelId'),
    loserImovelId: field(payload, 'loser_imovel_id', 'loserImovelId'),
    duplicateCandidateId: field(
      payload,
      'duplicate_candidate_id',
      'duplicateCandidateId'
    ),
    matchType: field(payload, 'match_type', 'matchType') || '',
    reversible: Boolean(field(payload, 'reversible')),
    appliedAt: field(payload, 'applied_at', 'appliedAt') || '',
    revertedAt: field(payload, 'reverted_at', 'revertedAt') || '',
    revertReason: field(payload, 'revert_reason', 'revertReason') || '',
    reviewNote: field(payload, 'review_note', 'reviewNote') || '',
    preview: normalizeObject(field(payload, 'preview')),
    affectedReferences: normalizeObject(
      field(payload, 'affected_references', 'affectedReferences')
    ),
    revertPlan: field(payload, 'revert_plan', 'revertPlan') || [],
  };
};

export const normalizeCandidate = candidate => {
  const payload = normalizeObject(candidate);
  const primaryImovel = normalizeImovel(
    field(payload, 'primary_imovel', 'primaryImovel')
  );
  const duplicateImovel = normalizeImovel(
    field(payload, 'duplicate_imovel', 'duplicateImovel')
  );
  const matchScore = Number(field(payload, 'match_score', 'matchScore') || 0);
  const partial =
    Boolean(field(payload, 'partial')) ||
    primaryImovel.missing ||
    duplicateImovel.missing;

  return {
    id: payload.id,
    status: field(payload, 'status') || 'pending',
    resolution: field(payload, 'resolution') || '',
    matchType: field(payload, 'match_type', 'matchType') || '',
    matchScore,
    matchScoreLabel: matchScore ? `${Math.round(matchScore * 100)}%` : '-',
    detectorKey: field(payload, 'detector_key', 'detectorKey') || '',
    detectorVersion:
      field(payload, 'detector_version', 'detectorVersion') || '',
    autoMergeable: Boolean(field(payload, 'auto_mergeable', 'autoMergeable')),
    requiresHumanReview: Boolean(
      field(payload, 'requires_human_review', 'requiresHumanReview')
    ),
    signals: normalizeObject(field(payload, 'signals')),
    discriminators: normalizeObject(field(payload, 'discriminators')),
    metadata: normalizeObject(field(payload, 'metadata')),
    reviewNote: field(payload, 'review_note', 'reviewNote') || '',
    reviewedAt: field(payload, 'reviewed_at', 'reviewedAt') || '',
    createdAt: field(payload, 'created_at', 'createdAt') || '',
    updatedAt: field(payload, 'updated_at', 'updatedAt') || '',
    primaryImovel,
    duplicateImovel,
    partial,
    partialReason: field(payload, 'partial_reason', 'partialReason') || '',
    latestMergeOperation: normalizeMergeOperation(
      field(payload, 'latest_merge_operation', 'latestMergeOperation')
    ),
  };
};

export const normalizeDuplicateQueue = payload => {
  const data = Array.isArray(payload?.data) ? payload.data : [];
  const meta = normalizeObject(payload?.meta);

  return {
    candidates: data.map(normalizeCandidate),
    meta: {
      count: Number(meta.count || 0),
      page: Number(meta.page || 1),
      perPage: Number(meta.per_page || meta.perPage || 50),
      totalPages: Number(meta.total_pages || meta.totalPages || 1),
      statusCounts: normalizeObject(meta.status_counts || meta.statusCounts),
      partial: Boolean(meta.partial),
      partialCount: Number(meta.partial_count || meta.partialCount || 0),
      partialMessage:
        meta.partial_message ||
        meta.partialMessage ||
        'Alguns candidatos vieram parciais.',
    },
  };
};

export const mergePlanPayload = ({ candidate, reason }) => ({
  duplicate_candidate_id: candidate.id,
  winner_imovel_id: candidate.primaryImovel.id,
  loser_imovel_id: candidate.duplicateImovel.id,
  review_note: reason,
});

export const mergeActionError = error => {
  const status = error?.response?.status;
  const data = normalizeObject(error?.response?.data);
  const errors = Array.isArray(data.errors)
    ? data.errors
    : [data.error, data.message].filter(Boolean);
  const message = errors.length
    ? errors.join(' ')
    : 'Nao foi possivel concluir a operacao.';

  return {
    status,
    code: data.code || '',
    message,
    conflict: status === 409 || data.code === 'merge_conflict',
  };
};
